import { useEffect, useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SensitiveDocumentPreview, SensitiveText } from "@/components/common/Sensitive";
import { invoicePdfIframeSrc } from "@/features/invoices/upload/invoicePdfEmbedUrl";
import { api } from "@/services/api";
import { cn } from "@/lib/utils";

export interface GraphAttachmentRef {
  mailboxId: string;
  messageId: string;
  attachmentId: string;
  filename: string;
}

/** True for filenames the in-app dialog can render inline (PDF / image). */
export function isPreviewableGraphAttachmentFilename(filename: string): boolean {
  return /\.(pdf|png|jpe?g|gif|webp|avif|bmp)$/i.test(filename);
}

interface GraphAttachmentPdfDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The attachment to preview; `null` while nothing is selected. */
  attachment: GraphAttachmentRef | null;
  /** Shown under the filename in the dialog header. */
  previewDescription?: string;
}

function isPdf(blobType: string, filename: string): boolean {
  return (
    blobType === "application/pdf" || filename.toLowerCase().endsWith(".pdf")
  );
}

function isImage(blobType: string, filename: string): boolean {
  if (blobType.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|avif|bmp|svg)$/i.test(filename);
}

/**
 * On-demand in-app preview of a Graph-mail billing attachment (carrier invoice
 * or agent debit/credit note). The bytes are fetched only when the dialog opens
 * and the object URL is revoked on close, so no PDF is loaded until the user
 * explicitly asks to view it.
 */
export function GraphAttachmentPdfDialog({
  open,
  onOpenChange,
  attachment,
  previewDescription = "Invoice document preview",
}: GraphAttachmentPdfDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [kind, setKind] = useState<"pdf" | "image" | "other">("other");
  const [downloading, setDownloading] = useState(false);

  // Stable key so the fetch only re-runs when the target attachment changes.
  const attachmentKey = attachment
    ? `${attachment.mailboxId}/${attachment.messageId}/${attachment.attachmentId}`
    : null;

  useEffect(() => {
    if (!open || !attachment) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    setLoading(true);
    setError(null);
    setObjectUrl(null);

    api
      .fetchGraphAttachmentBlob(
        attachment.mailboxId,
        attachment.messageId,
        attachment.attachmentId,
        attachment.filename,
      )
      .then(({ blob, filename }) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
        setKind(
          isPdf(blob.type, filename)
            ? "pdf"
            : isImage(blob.type, filename)
              ? "image"
              : "other",
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Failed to load document",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attachmentKey]);

  const handleDownload = async () => {
    if (!attachment) return;
    setDownloading(true);
    try {
      await api.downloadGraphAttachment(
        attachment.mailboxId,
        attachment.messageId,
        attachment.attachmentId,
        attachment.filename,
      );
    } catch (err) {
      console.warn("Failed to download attachment", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[6%] flex max-h-[88vh] w-[min(72rem,calc(100vw-2rem))] flex-col gap-3 rounded-xl border border-border bg-card p-3 shadow-2xl">
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-center gap-2 truncate">
            <FileText className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate">
              <SensitiveText value={attachment?.filename ?? "Document"} />
            </span>
          </DialogTitle>
          <DialogDescription>{previewDescription}</DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "flex min-h-[60vh] flex-1 items-center justify-center overflow-auto rounded-lg border border-border/60 bg-background/40",
          )}
        >
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading document…
            </div>
          ) : error ? (
            <p className="px-6 py-10 text-center text-sm text-destructive">
              {error}
            </p>
          ) : objectUrl && kind === "pdf" ? (
            <SensitiveDocumentPreview className="h-[70vh] w-full">
              <iframe
                title={attachment?.filename ?? "Document"}
                src={invoicePdfIframeSrc(objectUrl)}
                className="h-[70vh] w-full rounded-md border-0 bg-white"
              />
            </SensitiveDocumentPreview>
          ) : objectUrl && kind === "image" ? (
            <SensitiveDocumentPreview className="max-h-[70vh] w-full">
              <img
                src={objectUrl}
                alt={attachment?.filename ?? "Document"}
                className="max-h-[70vh] w-auto rounded-md object-contain"
              />
            </SensitiveDocumentPreview>
          ) : objectUrl ? (
            <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
              <FileText className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                This file can’t be previewed inline. Download it to open.
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleDownload()}
            disabled={downloading || !attachment}
          >
            {downloading ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GraphAttachmentPdfDialog;
